// Self-contained interactive HTML export of a curated audit snapshot, written for the
// OWNER/DIRECTOR: it opens by answering "como está a minha obra?" in plain language and
// keeps the technical tables as collapsible support material. Cartesian visual standard
// (Poppins, azul #2F54EB, acento #FF3300, grafismo de linhas 3D).
// Dismissed findings are excluded; the file works offline (Poppins degrades to system fonts).

export const buildPayload = (report, meta) => ({
  meta: {
    companyName: meta.companyName || "",
    buildingName: meta.buildingName || "",
    measurementNumber: report.measurement_number,
    measurementDate: report.measurement_date,
    generatedAt: report.generated_at,
    status: report.status,
    dismissedCount: report.findings.filter((f) => f.status === "dismissed").length,
  },
  summary: report.summary,
  findings: report.findings.filter((f) => f.status !== "dismissed"),
  tables: report.tables,
});

export const escapeHtml = (value) => String(value ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

// NOTE: the embedded script avoids backticks and "${" so the outer template stays simple.
const TEMPLATE_CSS = `
:root{--paper:#FFFFFF;--soft:#F4F6FB;--ink:#231F20;--muted:#94A3B8;--line:#E6E6E6;
--blue:#2F54EB;--blue-dark:#0F172A;--blue-soft:#EAF0FF;--accent:#FF3300;--accent-soft:#FFEDE8;
--warn:#FF8000;--warn-soft:#FFF3E6;--ok:#1B9E4B;--ok-soft:#E8F6EE;--mono:Consolas,Menlo,monospace}
*{box-sizing:border-box}
body{background:var(--paper);color:var(--ink);margin:0;
font:15px/1.65 Poppins,Montserrat,"Segoe UI",system-ui,sans-serif}
.wrap{max-width:980px;margin:0 auto;padding:0 24px 70px}
.hero{background:var(--blue-dark);color:#fff;margin:0 -24px;padding:44px 24px 36px;position:relative;overflow:hidden}
.hero svg.grid3d{position:absolute;inset:0;width:100%;height:100%;opacity:.16}
.hero .inner{position:relative;max-width:932px;margin:0 auto}
.brandmark{display:flex;align-items:center;gap:10px;margin-bottom:22px}
.brandmark .sq{width:16px;height:16px;border-radius:4px;border:2.5px solid #fff;display:inline-block}
.brandmark .sq+.sq{margin-left:-9px;border-color:var(--blue);background:transparent}
.brandmark b{font-size:13px;letter-spacing:.18em;font-weight:600}
.hero .eyebrow{font-size:11px;letter-spacing:.22em;text-transform:uppercase;color:#9DB4FF;font-weight:600}
.hero h1{font-size:34px;line-height:1.15;margin:6px 0 4px;font-weight:700}
.hero .sub{color:#C7D4FF;font-size:14px;margin-bottom:20px}
.verdict{border-radius:12px;padding:18px 20px;font-size:16.5px;line-height:1.6;background:rgba(255,255,255,.07);
border:1px solid rgba(255,255,255,.18)}
.verdict b{font-weight:700}
.verdict .tag{display:inline-block;font-size:11px;letter-spacing:.1em;text-transform:uppercase;font-weight:700;
border-radius:20px;padding:3px 12px;margin-bottom:8px}
.tag.ok{background:var(--ok);color:#fff}.tag.warn{background:var(--warn);color:#fff}.tag.crit{background:var(--accent);color:#fff}
h2{font-size:21px;font-weight:700;color:var(--blue-dark);margin:40px 0 6px}
h2 .n{color:var(--blue);font-family:var(--mono);font-weight:600;margin-right:6px}
.lead{color:#4b5563;font-size:14px;margin:0 0 18px;max-width:72ch}
.chapters{display:grid;grid-template-columns:1fr 1fr;gap:14px}
@media(max-width:760px){.chapters{grid-template-columns:1fr}}
.chapter{background:var(--soft);border:1px solid var(--line);border-radius:12px;padding:18px 20px}
.chapter .cap{font-size:10.5px;letter-spacing:.18em;text-transform:uppercase;color:var(--blue);font-weight:700}
.chapter .big{font-size:30px;font-weight:700;color:var(--blue-dark);margin:4px 0 0;font-family:Poppins,var(--mono)}
.chapter .big small{font-size:14px;color:var(--muted);font-weight:500}
.chapter .phrase{font-weight:600;font-size:14.5px;margin:8px 0 6px}
.chapter .phrase.ok{color:var(--ok)}.chapter .phrase.warn{color:var(--warn)}.chapter .phrase.crit{color:var(--accent)}
.chapter p{margin:0;font-size:13.5px;color:#4b5563}
.finding{border:1px solid var(--line);border-left:5px solid var(--line);border-radius:10px;padding:16px 18px;margin-bottom:12px;background:#fff}
.finding.critical{border-left-color:var(--accent);background:linear-gradient(0deg,var(--accent-soft),#fff 55%)}
.finding.warning{border-left-color:var(--warn)}
.finding.info{border-left-color:var(--blue)}
.finding .head{display:flex;gap:12px;align-items:baseline}
.finding .head b{flex:1;font-size:15px}
.finding .head span{font-family:var(--mono);font-weight:700;white-space:nowrap;color:var(--blue-dark)}
.finding.critical .head span{color:var(--accent)}
.finding p{margin:6px 0 0;color:#374151;font-size:13.5px}
.finding .note{margin-top:8px;font-size:12.5px;color:var(--muted);border-top:1px dashed var(--line);padding-top:7px}
.finding .note b{color:#6b7280}
.more-line{font-size:13px;color:var(--muted);margin:6px 0 0;padding-left:4px}
.goodbox{background:var(--ok-soft);border:1px solid #cde9d8;border-radius:12px;padding:16px 20px;margin-bottom:12px}
.goodbox b{color:var(--ok)}
.goodbox p{margin:4px 0 0;font-size:13.5px;color:#2d5a3f}
.gloss{display:grid;grid-template-columns:1fr 1fr;gap:8px 24px;background:var(--soft);border:1px solid var(--line);
border-radius:12px;padding:16px 20px;font-size:13px}
@media(max-width:760px){.gloss{grid-template-columns:1fr}}
.gloss div b{color:var(--blue-dark)}
details.support{border:1px solid var(--line);border-radius:12px;margin-bottom:10px;background:#fff}
details.support>summary{cursor:pointer;padding:14px 18px;font-weight:600;color:var(--blue-dark);list-style:none;display:flex;justify-content:space-between}
details.support>summary::after{content:"+";color:var(--blue);font-size:18px;font-weight:700}
details.support[open]>summary::after{content:"–"}
details.support .body{padding:4px 18px 18px}
table{border-collapse:collapse;width:100%;font-size:13px}
th{font-size:10.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--muted);text-align:left;
padding:7px 8px;border-bottom:2px solid var(--blue-dark);white-space:nowrap}
th.num,td.num{text-align:right;font-family:var(--mono)}
th[data-sort]{cursor:pointer}
td{padding:6px 8px;border-bottom:1px solid var(--line);vertical-align:top}
tr:hover td{background:var(--blue-soft)}
.wbs{font-family:var(--mono);font-size:12px;color:var(--muted);white-space:nowrap}
.neg{color:var(--accent);font-weight:600}.pos{color:var(--ok)}
.controls{display:flex;gap:8px;margin:8px 0 12px}
.controls input{border:1px solid var(--line);background:var(--soft);padding:7px 12px;font:inherit;width:320px;border-radius:8px}
.scroll{overflow-x:auto}
canvas{width:100%;height:210px;display:block}
.legend{display:flex;gap:16px;font-size:12px;color:var(--muted);margin-top:6px}
.legend i{display:inline-block;width:10px;height:10px;margin-right:5px}
.hbar{display:grid;grid-template-columns:200px 1fr 120px;gap:10px;align-items:center;font-size:13px;padding:4px 0}
.hbar .trk{height:13px;background:var(--soft);border:1px solid var(--line);display:flex;border-radius:3px;overflow:hidden}
.hbar .trk .a{background:var(--blue)}.hbar .trk .p{background:var(--warn)}
.hbar .v{font-family:var(--mono);text-align:right;font-size:12px}
footer{font-size:12px;color:var(--muted);margin-top:36px;border-top:1px solid var(--line);padding-top:14px}
footer .tagline{color:var(--blue);font-weight:600}
`;

const GRID3D_SVG =
  '<svg class="grid3d" viewBox="0 0 1000 320" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">' +
  ["0,0", "125,0", "250,0", "375,0", "500,0", "625,0", "750,0", "875,0", "1000,0", "0,80", "0,160", "0,240", "1000,80", "1000,160", "1000,240"]
    .map((p) => `<line x1="${p.split(",")[0]}" y1="${p.split(",")[1]}" x2="500" y2="320" stroke="#fff" stroke-width="1"/>`)
    .join("") +
  "</svg>";

const TEMPLATE_JS = `
var D = window.__AUDIT__;
var fmt = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 });
function R(v) { return v == null ? "—" : "R$ " + fmt.format(v); }
function Rm(v) {
  if (v == null) return "—";
  if (Math.abs(v) >= 1e6) return "R$ " + (v / 1e6).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + " milhões";
  return "R$ " + fmt.format(Math.round(v / 1000)) + " mil";
}
function pct1(v) { return v == null ? "—" : v.toFixed(1).replace(".", ",") + "%"; }
function esc(s) {
  return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) {
    return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c];
  });
}
var s = D.summary;

// ---- derived, director-facing measures --------------------------------------
var delayPp = (s.physicalPlannedPct != null && s.physicalActualPct != null)
  ? Math.round((s.physicalPlannedPct - s.physicalActualPct) * 10) / 10 : null;
var trendPct = (s.finalCostTrend != null && s.budget)
  ? Math.round((s.finalCostTrend / s.budget - 1) * 10000) / 100 : null;
var idcCents = s.idc == null ? null : Math.round(s.idc * 100);
var lost = (s.idc != null && s.paid != null) ? s.paid * (1 - s.idc) : null;
var toContract = (s.budget != null && s.committed != null) ? s.budget - s.committed : null;
var stretched = s.scheduleEndCurrent && s.scheduleEndContract && s.scheduleEndCurrent > s.scheduleEndContract;

function lvl3(v, warnAt, critAt) { // returns "ok"|"warn"|"crit"
  if (v == null) return "warn";
  if (v > critAt) return "crit";
  if (v > warnAt) return "warn";
  return "ok";
}
var execLvl = lvl3(delayPp, 3, 7);
var effLvl = s.idc == null ? "warn" : (s.idc < 0.9 ? "crit" : (s.idc < 0.95 ? "warn" : "ok"));
var projLvl = lvl3(trendPct, 2, 5);
var overall = [execLvl, effLvl, projLvl].indexOf("crit") >= 0 ? "crit"
  : ([execLvl, effLvl, projLvl].indexOf("warn") >= 0 ? "warn" : "ok");
var OVERALL_TAG = { ok: "Obra sob controle", warn: "Obra sob controle, com pontos de atenção", crit: "Obra exige ação imediata" };

// ---- header -----------------------------------------------------------------
document.getElementById("title").textContent = D.meta.buildingName;
document.getElementById("sub").textContent =
  D.meta.companyName + " · Auditoria de custos · Medição " + D.meta.measurementNumber +
  " (dados de " + D.meta.measurementDate + ")";

// ---- the answer: "como está a sua obra?" ------------------------------------
var vp = "";
vp += "Sua obra está com <b>" + pct1(s.physicalActualPct) + " executados</b>";
if (delayPp != null) {
  if (delayPp > 1) vp += ", <b>" + String(delayPp).replace(".", ",") + " pontos atrás</b> do cronograma (" + pct1(s.physicalPlannedPct) + " previstos)";
  else if (delayPp < -1) vp += ", <b>adiantada</b> em relação ao cronograma";
  else vp += ", em linha com o cronograma";
}
if (stretched) vp += " — e o cronograma corrente já projeta término em <b>" + s.scheduleEndCurrent + "</b>, além do contratado (" + s.scheduleEndContract + ")";
vp += ". ";
if (idcCents != null) {
  vp += "De cada R$ 1,00 que saiu do caixa, <b>R$ 0," + String(idcCents).padStart(2, "0") + " viraram obra pronta</b>";
  if (lost != null && lost > 0 && s.idc < 0.98) vp += " — há " + Rm(lost) + " pagos ainda sem obra medida correspondente";
  vp += ". ";
}
if (trendPct != null) {
  if (trendPct > 0.5) vp += "Mantido o ritmo atual, a projeção é fechar em <b>" + Rm(s.finalCostTrend) + "</b>, cerca de <b>" + String(trendPct).replace(".", ",") + "% acima do orçamento</b>.";
  else if (trendPct < -0.5) vp += "Mantido o ritmo, a projeção é fechar em <b>" + Rm(s.finalCostTrend) + "</b>, abaixo do orçamento.";
  else vp += "Mantido o ritmo, a projeção é fechar dentro do orçamento (" + Rm(s.finalCostTrend) + ").";
}
document.getElementById("verdict").innerHTML =
  '<span class="tag ' + overall + '">' + OVERALL_TAG[overall] + "</span><br>" + vp;

// ---- 4 chapters ---------------------------------------------------------------
function chapter(cap, big, small, lvl, phrase, body) {
  return '<div class="chapter"><div class="cap">' + cap + '</div><div class="big">' + big +
    (small ? ' <small>' + small + "</small>" : "") + '</div><div class="phrase ' + lvl + '">' + phrase +
    "</div><p>" + body + "</p></div>";
}
var ch = "";
ch += chapter("Capítulo 1 · Execução", pct1(s.physicalActualPct), "de obra pronta", execLvl,
  delayPp == null ? "Sem medição física disponível" :
    (delayPp > 7 ? "Bem atrás do combinado" : delayPp > 3 ? "Atrás do combinado" : delayPp < -1 ? "À frente do combinado" : "No ritmo combinado"),
  "O planejado para esta data era " + pct1(s.physicalPlannedPct) + "." +
  (stretched ? " Cada mês além de " + s.scheduleEndContract + " é canteiro, equipe e administração pagos a mais — atraso vira custo mesmo sem nenhum preço subir." : " Prazo em dia protege o custo indireto."));
ch += chapter("Capítulo 2 · Eficiência", idcCents == null ? "—" : "R$ 0," + String(idcCents).padStart(2, "0"), "de obra por R$ 1,00 pago", effLvl,
  s.idc == null ? "Sem indicador" : (s.idc < 0.9 ? "Dinheiro saindo mais rápido que a obra cresce" : s.idc < 0.95 ? "Pagando um pouco à frente da obra" : "Pagamento e produção andando juntos"),
  lost != null && lost > 0
    ? "São " + Rm(lost) + " pagos ainda sem obra medida. Parte é normal (material comprado e não aplicado, antecipações); parte pode ser ineficiência. É o número para acompanhar todo mês."
    : "O que foi pago tem obra medida correspondente.");
ch += chapter("Capítulo 3 · Compromissos", pct1(s.budget ? (s.committed / s.budget) * 100 : null), "do orçamento já comprometido", toContract != null && toContract > 0 ? "warn" : "ok",
  toContract != null && toContract > 0 ? Rm(toContract) + " ainda por contratar" : "Contratação encerrada",
  "Comprometido é o que já tem pedido ou contrato assinado (" + Rm(s.committed) + "). O que falta contratar será fechado a preços de mercado futuros — por isso comparamos tudo também contra o orçamento corrigido pelo CUB (" + Rm(s.budgetIndexed) + ").");
ch += chapter("Capítulo 4 · Projeção", trendPct == null ? "—" : (trendPct > 0 ? "+" : "") + String(trendPct).replace(".", ",") + "%", "sobre o orçamento", projLvl,
  trendPct == null ? "Sem projeção" : trendPct > 5 ? "Estouro relevante à vista" : trendPct > 2 ? "Fechamento acima do orçado" : "Dentro do orçamento",
  "Projeção de custo final: " + Rm(s.finalCostTrend) + " para um orçamento de " + Rm(s.budget) + ". A projeção assume o desempenho atual — cada ação nos itens abaixo muda este número.");
document.getElementById("chapters").innerHTML = ch;

// ---- attention: curated findings ---------------------------------------------
function findingCard(f) {
  return '<div class="finding ' + f.severity + '"><div class="head"><b>' + esc(f.title) + "</b>" +
    (f.valueIndexed != null ? "<span>" + R(f.valueIndexed) + "</span>" : "") + "</div><p>" + esc(f.narrative) + "</p>" +
    (f.curatorNote ? '<div class="note"><b>Nota da equipe:</b> ' + esc(f.curatorNote) + "</div>" : "") + "</div>";
}
var crit = D.findings.filter(function (f) { return f.severity === "critical"; });
var warns = D.findings.filter(function (f) { return f.severity === "warning"; })
  .sort(function (a, b) { return (b.valueIndexed || b.valueNominal || 0) - (a.valueIndexed || a.valueNominal || 0); });
var infos = D.findings.filter(function (f) { return f.severity === "info"; });
var goods = D.findings.filter(function (f) { return f.severity === "positive"; });

var TOP_WARN = 6;
var ah = crit.map(findingCard).join("") + warns.slice(0, TOP_WARN).map(findingCard).join("");
var rest = warns.slice(TOP_WARN);
if (rest.length) {
  var restTotal = rest.reduce(function (acc, f) { return acc + (f.valueIndexed || f.valueNominal || 0); }, 0);
  ah += '<div class="more-line">+ ' + rest.length + " outros pontos de atenção menores, somando " + Rm(restTotal) +
    " — a lista completa está nas seções técnicas ao final.</div>";
}
document.getElementById("attention").innerHTML = ah ||
  '<div class="goodbox"><b>Nenhum ponto crítico ou de atenção.</b><p>As regras de auditoria não encontraram desvios relevantes nesta medição.</p></div>';

document.getElementById("good").innerHTML =
  goods.map(function (f) { return '<div class="goodbox"><b>' + esc(f.title) + "</b><p>" + esc(f.narrative) + "</p></div>"; }).join("") +
  infos.map(function (f) { return '<div class="finding info"><div class="head"><b>' + esc(f.title) + "</b>" + (f.valueNominal ? "<span>" + R(f.valueNominal) + "</span>" : "") + "</div><p>" + esc(f.narrative) + "</p></div>"; }).join("");

// ---- monthly curve -------------------------------------------------------------
var curveDrawn = false;
function drawCurve() {
  if (curveDrawn) return;
  var cv = document.getElementById("curve");
  var pts = (s.monthlyPaidCurve || []).filter(function (e) { return e.year > 0; });
  if (!pts.length || !cv.clientWidth) return;
  curveDrawn = true;
  var dpr = window.devicePixelRatio || 1, W = cv.clientWidth, H = 210;
  cv.width = W * dpr; cv.height = H * dpr;
  var ctx = cv.getContext("2d"); ctx.scale(dpr, dpr);
  var acc = 0;
  var cum = pts.map(function (e) { acc += e.value; return acc; });
  var maxB = Math.max.apply(null, pts.map(function (e) { return e.value; }));
  var padL = 8, padR = 8, padT = 12, padB = 26, iw = (W - padL - padR) / pts.length;
  ctx.strokeStyle = "#EDF0F7";
  for (var g = 1; g <= 3; g++) {
    var gy = padT + (H - padT - padB) * g / 4;
    ctx.beginPath(); ctx.moveTo(padL, gy); ctx.lineTo(W - padR, gy); ctx.stroke();
  }
  pts.forEach(function (e, i) {
    var h = (H - padT - padB) * (e.value / maxB);
    ctx.fillStyle = "#2F54EB";
    ctx.fillRect(padL + i * iw + 1, H - padB - h, Math.max(iw - 2, 1.5), h);
  });
  ctx.strokeStyle = "#231F20"; ctx.lineWidth = 2; ctx.beginPath();
  cum.forEach(function (v, i) {
    var x = padL + i * iw + iw / 2, y = padT + (H - padT - padB) * (1 - v / acc);
    if (i) ctx.lineTo(x, y); else ctx.moveTo(x, y);
  });
  ctx.stroke();
  ctx.fillStyle = "#94A3B8"; ctx.font = "10px Consolas,monospace";
  var step = Math.ceil(pts.length / 10);
  pts.forEach(function (e, i) {
    if (i % step === 0) ctx.fillText(String(e.month).padStart(2, "0") + "/" + String(e.year).slice(2), padL + i * iw, H - 8);
  });
}
document.getElementById("d-curve").addEventListener("toggle", drawCurve);

// ---- support tables -------------------------------------------------------------
document.getElementById("t-over").innerHTML = (D.tables.overruns || []).map(function (r) {
  var deltaIdx = r.committed - r.budgetIndexed;
  return "<tr><td class=\\"wbs\\">" + esc(r.wbs) + "</td><td>" + esc(r.description) + "</td><td>" + esc(r.unit) + "</td>" +
    "<td class=\\"num\\">" + R(r.budget) + "</td><td class=\\"num\\">" + R(r.budgetIndexed) + "</td>" +
    "<td class=\\"num\\">" + R(r.committed) + "</td><td class=\\"num neg\\">+" + R(deltaIdx).slice(3) + "</td></tr>";
}).join("");

var imax = Math.max.apply(null, (D.tables.resourceGroups || []).map(function (g) { return g.attended + g.pending; }).concat([1]));
document.getElementById("ins-bars").innerHTML = (D.tables.resourceGroups || []).slice(0, 14).map(function (g) {
  var name = g.group.length > 28 ? g.group.slice(0, 27) + "…" : g.group;
  return '<div class="hbar"><span title="' + esc(g.group) + '">' + esc(name) + "</span>" +
    '<div class="trk"><div class="a" style="width:' + (100 * g.attended / imax) + '%"></div>' +
    '<div class="p" style="width:' + (100 * g.pending / imax) + '%"></div></div>' +
    '<span class="v">' + R(g.attended + g.pending) + "</span></div>";
}).join("");
document.getElementById("t-ins").innerHTML = (D.tables.topResources || []).map(function (t) {
  return "<tr><td>" + esc(t.description) + "</td><td>" + esc(t.group) + "</td>" +
    "<td class=\\"num\\">" + R(t.attended) + "</td><td class=\\"num\\">" + R(t.pending) + "</td></tr>";
}).join("");

var cmax = Math.max.apply(null, (D.tables.creditors || []).map(function (c) { return c.value; }).concat([1]));
var paidTotal = s.paid || 1;
document.getElementById("cred-bars").innerHTML = (D.tables.creditors || []).slice(0, 12).map(function (c) {
  var name = c.name.length > 30 ? c.name.slice(0, 29) + "…" : c.name;
  return '<div class="hbar"><span title="' + esc(c.name) + '">' + esc(name) + "</span>" +
    '<div class="trk"><div class="a" style="width:' + (100 * c.value / cmax) + '%"></div></div>' +
    '<span class="v">' + R(c.value) + " · " + (100 * c.value / paidTotal).toFixed(1).replace(".", ",") + "%</span></div>";
}).join("");

var eapRows = (D.tables.eapSubsteps || []).map(function (r) {
  return {
    wbs: r.wbs, unit: r.unit, description: r.description, budget: r.budget,
    committed: r.committed, paid: r.paid, saldo: (r.budgetIndexed || 0) - r.committed,
  };
});
var eapQ = "", eapKey = "wbs", eapDir = 1;
function renderEap() {
  var rows = eapRows.filter(function (r) {
    return !eapQ || (r.description + " " + r.wbs + " " + r.unit).toLowerCase().indexOf(eapQ) >= 0;
  });
  rows.sort(function (a, b) {
    var va = a[eapKey], vb = b[eapKey];
    if (typeof va === "string") return va.localeCompare(vb) * eapDir;
    return ((va || 0) - (vb || 0)) * eapDir;
  });
  document.getElementById("t-eap").innerHTML = rows.slice(0, 400).map(function (r) {
    return "<tr><td class=\\"wbs\\">" + esc(r.wbs) + "</td><td>" + esc(r.description) + "</td><td>" + esc(r.unit) + "</td>" +
      "<td class=\\"num\\">" + R(r.budget) + "</td><td class=\\"num\\">" + R(r.committed) + "</td><td class=\\"num\\">" + R(r.paid) + "</td>" +
      "<td class=\\"num " + (r.saldo < 0 ? "neg" : "pos") + "\\">" + R(r.saldo) + "</td></tr>";
  }).join("");
  document.getElementById("eap-n").textContent = rows.length + " subetapas" + (rows.length > 400 ? " (mostrando 400)" : "");
}
renderEap();
document.getElementById("f-eap").addEventListener("input", function (e) { eapQ = e.target.value.toLowerCase(); renderEap(); });
Array.prototype.forEach.call(document.querySelectorAll("#h-eap th[data-sort]"), function (th) {
  th.addEventListener("click", function () {
    var k = th.getAttribute("data-sort");
    eapDir = eapKey === k ? -eapDir : (k === "wbs" ? 1 : -1);
    eapKey = k;
    renderEap();
  });
});

document.getElementById("foot-meta").textContent =
  "Auditoria gerada pelo Ampli em " + String(D.meta.generatedAt).slice(0, 10) +
  ", sobre a medição " + D.meta.measurementNumber + " (" + D.meta.measurementDate + ") do Sienge, com curadoria da equipe Cartesian." +
  (D.meta.dismissedCount ? " " + D.meta.dismissedCount + " apontamento(s) automático(s) foram verificados e descartados na curadoria." : "");
`;

export const buildHtml = (payload) => {
  const json = JSON.stringify(payload).replace(/</g, "\\u003c");
  return (
    "<!doctype html><html lang=\"pt-BR\"><head><meta charset=\"utf-8\">" +
    "<meta name=\"viewport\" content=\"width=device-width, initial-scale=1\">" +
    `<title>Auditoria — ${escapeHtml(payload.meta.buildingName)} · Cartesian</title>` +
    '<link rel="preconnect" href="https://fonts.googleapis.com">' +
    '<link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">' +
    `<style>${TEMPLATE_CSS}</style></head><body>` +
    '<div class="hero">' + GRID3D_SVG + '<div class="inner">' +
    '<div class="brandmark"><span class="sq"></span><span class="sq"></span><b>CARTESIAN ENGENHARIA</b></div>' +
    '<div class="eyebrow">Auditoria de obra</div><h1 id="title"></h1><div class="sub" id="sub"></div>' +
    '<div class="verdict" id="verdict"></div></div></div>' +
    '<div class="wrap">' +
    '<h2><span class="n">01</span>A história da sua obra em 4 capítulos</h2>' +
    '<p class="lead">Quatro perguntas que resumem qualquer obra: quanto está pronto, quanto rende cada real pago, quanto já está comprometido e onde ela vai parar. Os detalhes técnicos que sustentam cada número estão no final do relatório.</p>' +
    '<div class="chapters" id="chapters"></div>' +
    '<h2><span class="n">02</span>O que pede a sua atenção</h2>' +
    '<p class="lead">Apontamentos encontrados pela auditoria, já verificados pela equipe — cada um diz o que aconteceu, o tamanho do impacto e qual pergunta fazer. Estouros são sempre medidos contra o orçamento corrigido pelo CUB, para não confundir inflação com desperdício.</p>' +
    '<div id="attention"></div>' +
    '<h2><span class="n">03</span>O que está indo bem</h2>' +
    '<div id="good"></div>' +
    '<h2><span class="n">04</span>Como ler os números</h2>' +
    '<div class="gloss">' +
    "<div><b>Orçado</b> — o combinado no início, a preços da data-base. <b>Indexado</b> = o mesmo escopo corrigido pelo CUB até hoje.</div>" +
    "<div><b>Comprometido</b> — tudo que já tem pedido ou contrato assinado. É promessa de gasto, não caixa.</div>" +
    "<div><b>Pago</b> — o que já saiu do caixa.</div>" +
    "<div><b>Medido</b> — quanto de obra ficou pronta de verdade, em valor. É a régua física da obra.</div>" +
    "<div><b>IDC</b> — eficiência: quanto de obra pronta cada R$ 1,00 pago comprou até aqui.</div>" +
    "<div><b>Tendência</b> — projeção de custo final se o desempenho atual se mantiver.</div>" +
    "</div>" +
    '<h2><span class="n">05</span>Dados de suporte</h2>' +
    '<details class="support" id="d-curve"><summary>Curva mensal do pago</summary><div class="body">' +
    '<canvas id="curve" height="210"></canvas>' +
    '<div class="legend"><span><i style="background:#2F54EB"></i>Pago no mês</span><span><i style="background:#231F20"></i>Acumulado</span></div></div></details>' +
    '<details class="support"><summary>Itens com orçamento estourado</summary><div class="body">' +
    '<div class="scroll"><table><thead><tr><th>WBS</th><th>Subetapa</th><th>Unidade</th><th class="num">Orçado</th><th class="num">Orç. indexado</th><th class="num">Comprometido</th><th class="num">Estouro (idx)</th></tr></thead><tbody id="t-over"></tbody></table></div></div></details>' +
    '<details class="support"><summary>Insumos por grupo — entregue × a entregar</summary><div class="body"><div id="ins-bars"></div>' +
    '<details><summary>Top insumos individuais</summary><div class="scroll"><table><thead><tr><th>Insumo</th><th>Grupo</th><th class="num">Atendido</th><th class="num">Pendente</th></tr></thead><tbody id="t-ins"></tbody></table></div></details></div></details>' +
    '<details class="support"><summary>Concentração de fornecedores</summary><div class="body"><div id="cred-bars"></div></div></details>' +
    '<details class="support"><summary>Explorador da EAP (todas as subetapas)</summary><div class="body">' +
    '<div class="controls"><input id="f-eap" type="search" placeholder="Buscar subetapa, etapa ou código WBS…"></div>' +
    '<div class="scroll"><table><thead id="h-eap"><tr><th data-sort="wbs">WBS ↕</th><th>Subetapa</th><th>Unidade</th><th class="num" data-sort="budget">Orçado ↕</th><th class="num" data-sort="committed">Compr. ↕</th><th class="num" data-sort="paid">Pago ↕</th><th class="num" data-sort="saldo">Saldo idx ↕</th></tr></thead><tbody id="t-eap"></tbody></table></div>' +
    '<p class="lead" id="eap-n" style="margin-top:8px"></p></div></details>' +
    '<footer><span class="tagline">Cartesian Engenharia — engenharia que apoia a gestão de obra.</span><br>' +
    '<span id="foot-meta"></span></footer>' +
    "</div>" +
    `<script>window.__AUDIT__=${json};<\/script>` +
    `<script>${TEMPLATE_JS}<\/script></body></html>`
  );
};

export const downloadAuditHtml = (report, meta) => {
  const payload = buildPayload(report, meta);
  const html = buildHtml(payload);
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  const slug = (meta.buildingName || "obra").toLowerCase().replace(/[^a-z0-9]+/g, "-");
  a.download = `auditoria-${slug}-medicao-${report.measurement_number}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
};
